import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useOnClickOutside } from "../../utils/commonData";
import { useUserContext } from "../../context/UserContext";
import PatientResultsList from "./PatientResultsList";
import { getDoctorPatientsApi } from "../../services/apiService";

// Lazy load cities data
const citiesDataPromise = import("../cities.json");

// Process cities data to extract wilayas and communes
const processCitiesData = (citiesData) => {
  const wilayasMap = new Map();
  const communesMap = {};
  
  citiesData.forEach(city => {
    const wilayaCode = city.wilaya_code;
    const wilayaName = city.wilaya_name_ascii;
    const communeId = city.id;
    const communeName = city.commune_name_ascii;
    
    if (!wilayasMap.has(wilayaCode)) {
      wilayasMap.set(wilayaCode, {
        id: wilayaCode,
        name: wilayaName
      });
      communesMap[wilayaCode] = [];
    }
    
    const existingCommune = communesMap[wilayaCode].find(c => c.id === communeId);
    if (!existingCommune) {
      communesMap[wilayaCode].push({
        id: communeId,
        name: communeName
      });
    }
  });
  
  return {
    wilayas: Array.from(wilayasMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    communes: communesMap
  };
};

const PatientSearchWithFilters = ({ 
  showMobileSearch = false,
  onMRZScan
}) => {
  const { userData } = useUserContext();
  const patientSearchResultRef = useRef();
  
  const [searchPatientTerm, setSearchPatientTerm] = useState("");
  const [searchPatientList, setSearchPatientList] = useState([]);
  const [showPatientList, setShowPatientList] = useState(false);
  const [patientSearchTimeoutId, setPatientSearchTimeoutId] = useState(null);
  const [patientSearchLoad, setPatientSearchLoad] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [citiesData, setCitiesData] = useState(null);
  const [filters, setFilters] = useState({
    profileType: '',
    gender: '',
    wilayaId: '',
    communeId: '',
    age: '',
    myPatientsOnly: false
  });

  useOnClickOutside(patientSearchResultRef, () => setShowPatientList(false));

  // Load cities data when filters are shown
  useEffect(() => {
    if (showAdvancedFilters && !citiesData) {
      citiesDataPromise.then(module => {
        const processed = processCitiesData(module.default);
        setCitiesData(processed);
      }).catch(err => {
        console.error("Failed to load cities data:", err);
        toast.error("Failed to load location data", { autoClose: 1500 });
      });
    }
  }, [showAdvancedFilters, citiesData]);

  const handleSearchTextChange = (event) => {
    const value = event.target.value;
    setSearchPatientTerm(value);
    
    // Only show list if there's enough text
    if (value.length < 2) {
      setShowPatientList(false);
      setSearchPatientList([]);
      if (patientSearchTimeoutId) clearTimeout(patientSearchTimeoutId);
      return;
    }

    setShowPatientList(true);

    // Clear the existing timeout
    if (patientSearchTimeoutId) {
      clearTimeout(patientSearchTimeoutId);
    }

    // Set a new timeout for debouncing - Increased to 500ms for better performance on tablets
    const newTimeoutId = setTimeout(() => {
      getPatientDetailsList(value);
    }, 500);

    // Store the timeout ID
    setPatientSearchTimeoutId(newTimeoutId);
  };

  const getPatientDetailsList = async (value, filtersOverride = null) => {
    try {
      setPatientSearchLoad(true);
      const activeFilters = filtersOverride || filters;
      const doctorId = userData?.users?.id;

      if (!doctorId) {
        setPatientSearchLoad(false);
        return;
      }

      // Build query parameters for API call
      const params = {
        search: value || '',
        profileType: activeFilters.profileType,
        gender: activeFilters.gender,
        wilayaId: activeFilters.wilayaId,
        communeId: activeFilters.communeId,
        age: activeFilters.age,
        myPatientsOnly: activeFilters.myPatientsOnly
      };

      // Make API call with search term and filters
      const response = await getDoctorPatientsApi(doctorId, params);
      
      if (response.data?.success && response.data?.patients?.list) {
        setSearchPatientList(response.data.patients.list);
      } else {
        setSearchPatientList([]);
      }
      
      setPatientSearchLoad(false);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.", { autoClose: 1500 });
      setSearchPatientList([]);
      setPatientSearchLoad(false);
    }
  };

  const loadMRZData = async () => {
    try {
      const response = await fetch("http://localhost:5001/id-data?token=mySecret123");

      if (response.status === 204) {
        alert("No data available yet.");
        return;
      }

      if (!response.ok) {
        throw new Error("Unauthorized or server error");
      }

      const data = await response.json();

      if (data) {
        const fullName = data.name + " " + data.surname;
        setSearchPatientTerm(fullName);
        
        const syntheticEvent = {
          target: { value: fullName }
        };
        
        handleSearchTextChange(syntheticEvent);
        
        // Call parent callback if provided
        if (onMRZScan) {
          onMRZScan(data);
        }
      }
    } catch (err) {
      console.error(err);
      alert("Could not connect to the ID scanner. Make sure it's running.");
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    getPatientDetailsList(searchPatientTerm, newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      profileType: '',
      gender: '',
      wilayaId: '',
      communeId: '',
      age: '',
      myPatientsOnly: false
    };
    setFilters(clearedFilters);
    getPatientDetailsList(searchPatientTerm, clearedFilters);
  };

  return (
    <div className={`searchDiv search-filter ${showMobileSearch ? "mobileSearch" : ""}`} ref={patientSearchResultRef}>
      <div className="search-input-wrapper">
        <input 
          type="text" 
          placeholder="Search patient here" 
          value={searchPatientTerm} 
          onChange={handleSearchTextChange} 
          autoComplete="off"
        />
        <button className="searchicon2 id-card-btn" onClick={loadMRZData}>
          <span className="icon">🔍</span> ID Card
        </button>
      </div>

      <div className="search-loader" style={{ display: patientSearchLoad ? 'block' : 'none' }}>
        <CircularProgress size={15} />
      </div>

      <div className="search-results-enhanced" style={{ display: showPatientList ? 'block' : 'none' }}>
        {/* Advanced Filters Toggle */}
          <div className="filters-header">
            <button
              className="toggle-filters-btn"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              {showAdvancedFilters ? '− Hide Filters' : '+ Show Filters'}
            </button>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="advanced-filters-panel">
              {/* Compact Two-Column Layout */}
              <div className="filter-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {/* Gender */}
                <div className="filter-group">
                  <label>Gender</label>
                  <div className="button-group">
                    <div
                      className={`filter-btn ${filters.gender === 'M' ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters, gender: filters.gender === 'M' ? '' : 'M' };
                        handleFilterChange(newFilters);
                      }}
                    >
                      Male
                    </div>
                    <div
                      className={`filter-btn ${filters.gender === 'F' ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters, gender: filters.gender === 'F' ? '' : 'F' };
                        handleFilterChange(newFilters);
                      }}
                    >
                      Female
                    </div>
                  </div>
                </div>

                {/* Profile Type */}
                <div className="filter-group">
                  <label>Profile Type</label>
                  <div className="button-group">
                    <div
                      className={`filter-btn ${filters.profileType === 'local' ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters, profileType: filters.profileType === 'local' ? '' : 'local' };
                        handleFilterChange(newFilters);
                      }}
                    >
                      Local
                    </div>
                    <div
                      className={`filter-btn ${filters.profileType === 'national' ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters, profileType: filters.profileType === 'national' ? '' : 'national' };
                        handleFilterChange(newFilters);
                      }}
                    >
                      National
                    </div>
                  </div>
                </div>

                {/* Wilaya */}
                <div className="filter-group">
                  <label>Wilaya</label>
                  <select
                    value={filters.wilayaId}
                    onChange={(e) => {
                      const newFilters = { ...filters, wilayaId: e.target.value, communeId: '' };
                      handleFilterChange(newFilters);
                    }}
                    disabled={!citiesData}
                  >
                    <option value="">{citiesData ? 'Select Wilaya' : 'Loading...'}</option>
                    {citiesData?.wilayas.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>

                {/* Commune */}
                <div className="filter-group">
                  <label>Commune</label>
                  <select
                    value={filters.communeId}
                    onChange={(e) => {
                      const newFilters = { ...filters, communeId: e.target.value };
                      handleFilterChange(newFilters);
                    }}
                    disabled={!filters.wilayaId || !citiesData}
                  >
                    <option value="">Select Commune</option>
                    {filters.wilayaId && citiesData?.communes[filters.wilayaId]?.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Age - Full Width */}
                <div className="filter-group-full" style={{ gridColumn: '1 / -1' }}>
                  <label>Age or DOB</label>
                  <input
                    type="text"
                    placeholder="e.g., 25 or 1998-05-15"
                    value={filters.age}
                    onChange={(e) => setFilters({ ...filters, age: e.target.value })}
                    onBlur={() => getPatientDetailsList(searchPatientTerm)}
                    onKeyDown={(e) => e.key === 'Enter' && getPatientDetailsList(searchPatientTerm)}
                  />
                </div>

                {/* My Patients Only & Clear - Full Width */}
                <div className="filter-actions" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={filters.myPatientsOnly}
                      onChange={(e) => {
                        const newFilters = { ...filters, myPatientsOnly: e.target.checked };
                        handleFilterChange(newFilters);
                      }}
                    />
                    Show my patients only
                  </label>

                  <button
                    className="clear-filters-btn"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results List */}
          <PatientResultsList
            searchPatientList={searchPatientList}
            patientSearchLoad={patientSearchLoad}
            onPatientSelect={() => {
              setShowPatientList(false);
              setSearchPatientTerm("");
            }}
          />
        </div>
      
    </div>
  );
};

export default PatientSearchWithFilters;
